import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Sentry from '@sentry/browser';
import { MainButton } from 'elements/button/MainButton';
import { EFonts } from 'libs/styles/styling';
import * as React from 'react';
import Config from 'static/config/coreConfig';
import styled from 'styled-components/macro';

if (Config.Environment !== 'development') {
    Sentry.init({
        dsn: 'https://ac7b06aed2a44945b549890028bffc64@sentry.io/1387095',
        environment: Config.Environment
    });
}

export interface IProps {
    children: React.ReactNode | React.ReactNodeArray;
}

export interface IState {
    error: unknown;
    errorInfo: unknown;
}

export class ErrorBoundary extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.state = { error: null, errorInfo: null };
    }

    componentDidCatch(error: unknown, errorInfo: unknown): void {
        this.setState({ error, errorInfo });
        Sentry.withScope((scope) => {
            Object.keys(errorInfo).forEach((key) => {
                scope.setExtra(key, errorInfo[key]);
            });
            Sentry.captureException(error);
            console.log('Captured', error, errorInfo);
        });
    }
    openChat() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).HubSpotConversations?.widget?.open?.();
    }

    render(): JSX.Element | React.ReactNode {
        if (this.state.error) {
            //render fallback UI
            return (
                <S_ErrorPage>
                    <S_Error>
                        <S_Left>
                            <S_Icon icon={['far', 'burst']} />
                        </S_Left>
                        <S_Right>
                            <S_Title>Oh. There was an error.</S_Title>
                            <S_Text>
                                We are sorry. Please try refreshing the page or contact us.{' '}
                            </S_Text>
                            <S_Buttons>
                                <MainButton
                                    type={'secondary'}
                                    onClick={() => {
                                        this.openChat();
                                    }}
                                >
                                    Contact us
                                </MainButton>
                                <MainButton
                                    type={'primary'}
                                    onClick={() => {
                                        window.location.reload();
                                    }}
                                >
                                    Refresh page
                                </MainButton>
                            </S_Buttons>
                        </S_Right>
                    </S_Error>
                </S_ErrorPage>
            );
        } else {
            //when there's not an error, render children untouched
            return this.props.children;
        }
    }
}

const S_ErrorPage = styled.div`
    width: 100%;
    height: 100%;
    background-color: #e1e1e1;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const S_Error = styled.div`
    display: flex;
    background: linear-gradient(180deg, #323b7c 0%, #1d255e 100%);
    box-shadow: 10px 10px 50px rgba(0, 0, 0, 0.25);
    border-radius: 20px;
    padding: 60px 55px 50px 55px;
`;

const S_Left = styled.div`
    margin: 0 40px 0 0;
`;

const S_Icon = styled(FontAwesomeIcon)`
    color: white;
    font-size: 65px;
    margin: 5px 0 0 0;
`;

const S_Right = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_Title = styled.div`
    font-size: 25px;
    line-height: 31px;
    color: #ffffff;
    margin: 0 0 20px 0;
    font-weight: ${EFonts.Bold};
`;

const S_Text = styled.div`
    font-size: 16px;
    line-height: 20px;
    margin: 0 0 40px 0;
    color: #ffffff;
`;

const S_Buttons = styled.div`
    display: flex;
    justify-content: flex-end;
    & > *:first-child {
        margin: 0 20px 0 0;
    }
`;
